/**
 * Sunrise and sunset, computed from latitude and longitude.
 *
 * The pilot ships a hardcoded 14-row table of sunrise/sunset times for
 * Dádpuszta, computed offline once and pasted into the source. It works for
 * exactly one site in exactly one year, and a second festival needs a second
 * table someone has to remember to generate.
 *
 * This is the NOAA solar position algorithm, so any edition anywhere gets
 * correct times from its coordinates — and the table disappears.
 *
 * Accuracy is around a minute, which is far better than the badge needs: it
 * exists so a set that runs through sunrise gets a 🌅 and a warm glow.
 */
import { dayStart, toOffsetISO, wallClock } from './time.js';
import type { TimeZone, DayBucket } from './time.js';

const RAD = Math.PI / 180;
const DEG = 180 / Math.PI;

/** Days since the J2000.0 epoch. */
function julianCentury(date: Date): number {
  const jd = date.getTime() / 86_400_000 + 2440587.5;
  return (jd - 2451545) / 36525;
}

function geomMeanLongSun(t: number): number {
  return (280.46646 + t * (36000.76983 + t * 0.0003032)) % 360;
}
function geomMeanAnomalySun(t: number): number {
  return 357.52911 + t * (35999.05029 - 0.0001537 * t);
}
function eccentricityEarthOrbit(t: number): number {
  return 0.016708634 - t * (0.000042037 + 0.0000001267 * t);
}
function sunEqOfCentre(t: number): number {
  const m = geomMeanAnomalySun(t) * RAD;
  return (
    Math.sin(m) * (1.914602 - t * (0.004817 + 0.000014 * t)) +
    Math.sin(2 * m) * (0.019993 - 0.000101 * t) +
    Math.sin(3 * m) * 0.000289
  );
}
function sunApparentLong(t: number): number {
  const trueLong = geomMeanLongSun(t) + sunEqOfCentre(t);
  const omega = 125.04 - 1934.136 * t;
  return trueLong - 0.00569 - 0.00478 * Math.sin(omega * RAD);
}
function meanObliquityOfEcliptic(t: number): number {
  const seconds = 21.448 - t * (46.815 + t * (0.00059 - t * 0.001813));
  return 23 + (26 + seconds / 60) / 60;
}
function obliquityCorrection(t: number): number {
  return meanObliquityOfEcliptic(t) + 0.00256 * Math.cos((125.04 - 1934.136 * t) * RAD);
}
function sunDeclination(t: number): number {
  return (
    Math.asin(Math.sin(obliquityCorrection(t) * RAD) * Math.sin(sunApparentLong(t) * RAD)) * DEG
  );
}
function equationOfTime(t: number): number {
  const epsilon = obliquityCorrection(t) * RAD;
  const l0 = geomMeanLongSun(t) * RAD;
  const e = eccentricityEarthOrbit(t);
  const m = geomMeanAnomalySun(t) * RAD;
  const y = Math.tan(epsilon / 2) ** 2;
  const eTime =
    y * Math.sin(2 * l0) -
    2 * e * Math.sin(m) +
    4 * e * y * Math.sin(m) * Math.cos(2 * l0) -
    0.5 * y * y * Math.sin(4 * l0) -
    1.25 * e * e * Math.sin(2 * m);
  return eTime * 4 * DEG;
}

/**
 * The hour angle at sunrise, in degrees.
 *
 * The 90.833° zenith accounts for atmospheric refraction and the sun's
 * angular radius — sunrise is when the sun's UPPER LIMB touches the horizon,
 * not its centre.
 */
function hourAngleSunrise(lat: number, declination: number): number | null {
  const latRad = lat * RAD;
  const decRad = declination * RAD;
  const cosH =
    Math.cos(90.833 * RAD) / (Math.cos(latRad) * Math.cos(decRad)) - Math.tan(latRad) * Math.tan(decRad);
  if (cosH > 1 || cosH < -1) return null; // polar day or polar night
  return Math.acos(cosH) * DEG;
}

export interface SunTimes {
  /** Null inside the polar circles, where the sun may not rise or set at all. */
  readonly sunrise: Date | null;
  readonly sunset: Date | null;
  readonly solarNoon: Date;
}

/** Sunrise, sunset and solar noon for one local day at one location. */
export function sunTimes(
  day: DayBucket,
  lat: number,
  lng: number,
  timeZone: TimeZone,
): SunTimes {
  const localMidnight = dayStart(day, timeZone);
  const noonGuess = new Date(localMidnight.getTime() + 12 * 3600_000);
  const t = julianCentury(noonGuess);

  const eqTime = equationOfTime(t);
  const declination = sunDeclination(t);

  // Minutes from UTC midnight.
  const solarNoonUtcMinutes = 720 - 4 * lng - eqTime;
  const utcMidnight = Date.UTC(
    noonGuess.getUTCFullYear(), noonGuess.getUTCMonth(), noonGuess.getUTCDate(),
  );
  const solarNoon = new Date(utcMidnight + solarNoonUtcMinutes * 60_000);

  const ha = hourAngleSunrise(lat, declination);
  if (ha === null) return { sunrise: null, sunset: null, solarNoon };

  return {
    sunrise: new Date(utcMidnight + (solarNoonUtcMinutes - ha * 4) * 60_000),
    sunset: new Date(utcMidnight + (solarNoonUtcMinutes + ha * 4) * 60_000),
    solarNoon,
  };
}

/** Sunrise and sunset as `HH:MM` in the festival's own zone. */
export function sunTimeLabels(
  day: DayBucket,
  lat: number,
  lng: number,
  timeZone: TimeZone,
): { sunrise: string | null; sunset: string | null } {
  const { sunrise, sunset } = sunTimes(day, lat, lng, timeZone);
  return {
    sunrise: sunrise ? wallClock(sunrise, timeZone) : null,
    sunset: sunset ? wallClock(sunset, timeZone) : null,
  };
}

export interface SunEvent {
  readonly kind: 'sunrise' | 'sunset';
  readonly at: Date;
  readonly label: string;
}

/**
 * Which solar events fall inside an act's window.
 *
 * A set running through sunrise is one of the things people plan a festival
 * around, so the schedule marks it.
 */
export function sunEventsDuring(
  startAt: Date,
  endAt: Date,
  lat: number,
  lng: number,
  timeZone: TimeZone,
): SunEvent[] {
  const events: SunEvent[] = [];
  const DAY = 86_400_000;
  // Check the day before and after too: an act can span midnight.
  for (let t = startAt.getTime() - DAY; t <= endAt.getTime() + DAY; t += DAY) {
    const day = toOffsetISO(new Date(t), timeZone).slice(0, 10);
    const { sunrise, sunset } = sunTimes(day, lat, lng, timeZone);
    for (const [kind, at] of [['sunrise', sunrise], ['sunset', sunset]] as const) {
      if (!at) continue;
      if (at >= startAt && at <= endAt && !events.some((e) => e.at.getTime() === at.getTime())) {
        events.push({ kind, at, label: wallClock(at, timeZone) });
      }
    }
  }
  return events.sort((a, b) => a.at.getTime() - b.at.getTime());
}
